import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, ChevronLeft, ChevronRight, AlertTriangle, TrendingUp, Users } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine, Cell
} from "recharts";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const DAY_LABELS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

const startOfWeek = (d) => {
  const dt = new Date(d);
  dt.setHours(0, 0, 0, 0);
  const day = dt.getDay() || 7;
  if (day !== 1) dt.setDate(dt.getDate() - (day - 1));
  return dt;
};
const startOfMonth = (d) => {
  const dt = new Date(d);
  dt.setDate(1);
  dt.setHours(0, 0, 0, 0);
  return dt;
};
const endOfMonth = (d) => {
  const dt = new Date(d);
  dt.setMonth(dt.getMonth() + 1, 0);
  dt.setHours(0, 0, 0, 0);
  return dt;
};
const toISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const MONTHS_FR = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
                   "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

const WorkloadView = () => {
  const [mode, setMode] = useState("week"); // "week" | "month"
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [monthDate, setMonthDate] = useState(() => startOfMonth(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchWorkload = useCallback(async () => {
    try {
      setLoading(true);
      let start, end;
      if (mode === "week") {
        start = weekStart;
        end = new Date(weekStart);
        end.setDate(end.getDate() + 6);
      } else {
        start = monthDate;
        end = endOfMonth(monthDate);
      }
      const res = await axios.get(`${API_URL}/planning/workload`, {
        headers: getHeaders(),
        params: { start_date: toISO(start), end_date: toISO(end) }
      });
      setData(res.data);
    } catch (e) {
      toast.error("Erreur chargement charge ouvriers");
    } finally { setLoading(false); }
  }, [mode, weekStart, monthDate]);

  useEffect(() => { fetchWorkload(); }, [fetchWorkload]);

  const shift = (n) => {
    if (mode === "week") {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + n * 7);
      setWeekStart(startOfWeek(d));
    } else {
      const d = new Date(monthDate);
      d.setMonth(d.getMonth() + n);
      setMonthDate(startOfMonth(d));
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }
  if (!data) return null;

  const days = data.days || [];
  const workingDays = days.filter(d => !d.is_weekend && !d.is_holiday);
  const overloadCount = workingDays.filter(d => d.overload > 0).length;
  const maxReq = Math.max(...workingDays.map(d => d.required), 0);
  const totalReq = workingDays.reduce((s, d) => s + d.required, 0);
  const totalAvail = workingDays.reduce((s, d) => s + d.available, 0);
  const utilPct = totalAvail > 0 ? Math.round((Math.min(totalReq, totalAvail) / totalAvail) * 100) : 0;

  const chartData = (() => {
    if (mode === "week") {
      return days.map(d => {
        const dt = new Date(d.date);
        return {
          name: `${DAY_LABELS[(dt.getDay() + 6) % 7]} ${dt.getDate()}/${dt.getMonth() + 1}`,
          Capacite: d.is_weekend || d.is_holiday ? 0 : d.available,
          Charge: d.is_weekend || d.is_holiday ? 0 : d.required,
          overload: d.overload
        };
      });
    }
    // Month mode : aggregate working days per ISO week
    const weeks = {};
    for (const d of days) {
      if (d.is_weekend || d.is_holiday) continue;
      const dt = new Date(d.date);
      const ws = startOfWeek(dt);
      const key = toISO(ws);
      if (!weeks[key]) {
        const we = new Date(ws); we.setDate(we.getDate() + 4); // Friday
        weeks[key] = {
          name: `${ws.getDate()}/${ws.getMonth() + 1} - ${we.getDate()}/${we.getMonth() + 1}`,
          Capacite: 0, Charge: 0, overload: 0, workingDays: 0
        };
      }
      weeks[key].Capacite += d.available;
      weeks[key].Charge += d.required;
      weeks[key].overload += d.overload;
      weeks[key].workingDays += 1;
    }
    return Object.keys(weeks).sort().map(k => weeks[k]);
  })();

  const weekLabel = (() => {
    if (mode === "week") {
      const end = new Date(weekStart);
      end.setDate(end.getDate() + 6);
      return `Semaine du ${weekStart.toLocaleDateString("fr-FR")} au ${end.toLocaleDateString("fr-FR")}`;
    }
    return `${MONTHS_FR[monthDate.getMonth()]} ${monthDate.getFullYear()}`;
  })();

  return (
    <div className="space-y-4" data-testid="workload-view">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => shift(-1)} data-testid="workload-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <span className="font-heading font-bold text-sm min-w-[260px] text-center">{weekLabel}</span>
          <Button variant="outline" size="icon" onClick={() => shift(1)} data-testid="workload-next"><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => { setWeekStart(startOfWeek(new Date())); setMonthDate(startOfMonth(new Date())); }}>
            {mode === "week" ? "Cette semaine" : "Ce mois"}
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button variant={mode === "week" ? "default" : "outline"} size="sm" onClick={() => setMode("week")} data-testid="workload-mode-week">Semaine</Button>
          <Button variant={mode === "month" ? "default" : "outline"} size="sm" onClick={() => setMode("month")} data-testid="workload-mode-month">Mois</Button>
          <div className="text-sm text-muted-foreground ml-4">
            Équipe de référence : <span className="font-medium text-foreground">{data.total_ouvriers}</span>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="pt-4">
            <div className="text-xs text-muted-foreground">Jours surchargés</div>
            <div className={`text-2xl font-bold ${overloadCount > 0 ? "text-destructive" : "text-emerald-600"}`}>{overloadCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-xs text-muted-foreground">Charge cumulée (jours-homme)</div>
            <div className="text-2xl font-bold text-primary">{totalReq}</div>
            <div className="text-xs text-muted-foreground">sur {totalAvail} disponibles</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-xs text-muted-foreground">Taux d'occupation</div>
            <div className="text-2xl font-bold">{utilPct}%</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-xs text-muted-foreground">Pic de charge</div>
            <div className="text-2xl font-bold text-orange-600">{maxReq}</div>
            <div className="text-xs text-muted-foreground">ouvriers / jour</div>
          </CardContent>
        </Card>
      </div>

      {/* Histogram */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><TrendingUp className="h-5 w-5 text-primary" /> Histogramme hebdomadaire</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={chartData} barCategoryGap={12}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
              <Tooltip
                formatter={(value, name) => [value, name]}
                labelFormatter={(l) => l}
              />
              <Legend />
              {mode === "week" && (
                <ReferenceLine y={data.total_ouvriers} stroke="#f97316" strokeDasharray="4 4" label={{ value: "Effectif total", position: "insideTopRight", fill: "#f97316", fontSize: 11 }} />
              )}              <Bar dataKey="Capacite" name="Capacité disponible" fill="#0ea5e9">
                {chartData.map((_, i) => <Cell key={i} />)}
              </Bar>
              <Bar dataKey="Charge" name="Charge requise">
                {chartData.map((d, i) => (
                  <Cell key={i} fill={d.overload > 0 ? "#dc2626" : "#22c55e"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Day details */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Users className="h-5 w-5 text-primary" /> Détail par jour</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {days.map(d => {
            const dt = new Date(d.date);
            const dayLabel = DAY_LABELS[(dt.getDay() + 6) % 7];
            const dateLabel = dt.toLocaleDateString("fr-FR");
            return (
              <div
                key={d.date}
                className={`flex flex-wrap items-center gap-3 p-3 rounded-md border ${
                  d.is_weekend ? "bg-muted/30 opacity-60" : d.is_holiday ? "bg-red-50 border-red-200" :
                  d.overload > 0 ? "bg-red-50 border-red-300" : d.required > 0 ? "bg-emerald-50 border-emerald-200" : ""
                }`}
              >
                <div className="w-24 font-medium text-sm">{dayLabel} {dateLabel}</div>
                {d.is_weekend ? (
                  <Badge variant="outline" className="text-xs">Week-end</Badge>
                ) : d.is_holiday ? (
                  <Badge variant="outline" className="text-xs bg-red-100">Férié</Badge>
                ) : (
                  <>
                    <div className="flex items-center gap-1 text-sm">
                      <span className="text-muted-foreground">Capacité</span>
                      <Badge variant="outline">{d.available}/{data.total_ouvriers}</Badge>
                    </div>
                    <div className="flex items-center gap-1 text-sm">
                      <span className="text-muted-foreground">Charge</span>
                      <Badge variant={d.overload > 0 ? "destructive" : "secondary"}>{d.required}</Badge>
                    </div>
                    {d.overload > 0 && (
                      <Badge variant="destructive" className="gap-1">
                        <AlertTriangle className="h-3 w-3" />
                        Surcharge +{d.overload}
                      </Badge>
                    )}
                    <div className="flex flex-wrap gap-1 ml-auto">
                      {d.orders.map(o => (
                        <Badge key={o.id} variant="outline" className="text-xs">
                          {o.reference} ({o.executants})
                        </Badge>
                      ))}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
};

export default WorkloadView;
